"use client";

import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { NextIntlClientProvider } from "next-intl";
import englishMessages from "../../../messages/en.json";
import spanishMessages from "../../../messages/es.json";

export type ConsumerLocale = "es" | "en";

interface ConsumerLocaleContextValue {
  locale: ConsumerLocale;
  setLocale: (locale: ConsumerLocale) => void;
}

export const CONSUMER_LOCALE_STORAGE_KEY = "gruv_consumer_locale";
const LocaleContext = createContext<ConsumerLocaleContextValue | null>(null);
const messages = { es: spanishMessages, en: englishMessages } as const;

/** Reads a valid browser preference with compatibility for the prior locale cookie. */
export function storedConsumerLocale(
  storage: Pick<Storage, "getItem"> = window.localStorage,
  cookieHeader: string = typeof document !== "undefined" ? document.cookie : "",
): ConsumerLocale {
  const stored = storage.getItem(CONSUMER_LOCALE_STORAGE_KEY);
  if (stored === "es" || stored === "en") return stored;
  const cookie = cookieHeader.split(";").map((part) => part.trim()).find((part) => part.startsWith("NEXT_LOCALE="));
  return cookie?.split("=")[1] === "en" ? "en" : "es";
}

/** Provides bundled translations without a request-time Next.js server. */
export function ConsumerIntlProvider({ children }: PropsWithChildren) {
  const [locale, setLocaleState] = useState<ConsumerLocale>("es");

  useEffect(() => setLocaleState(storedConsumerLocale()), []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<ConsumerLocaleContextValue>(() => ({
    locale,
    setLocale: (nextLocale) => {
      window.localStorage.setItem(CONSUMER_LOCALE_STORAGE_KEY, nextLocale);
      document.cookie = `NEXT_LOCALE=${nextLocale}; Path=/; Max-Age=31536000; SameSite=Lax`;
      setLocaleState(nextLocale);
    },
  }), [locale]);

  return (
    <LocaleContext.Provider value={value}>
      <NextIntlClientProvider locale={locale} messages={messages[locale]} timeZone="America/Argentina/Buenos_Aires">
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

/** Returns the active Consumer locale and update function. */
export function useConsumerLocale(): ConsumerLocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useConsumerLocale must be used inside ConsumerIntlProvider");
  return context;
}
